
import { useState } from 'react';
import { ImageType } from '@/components/image-generator/FilterBar/TypeFilter';
import { FilterOption } from '@/components/ui/FilterDropdown';
import { UseImageFiltersReturn } from './types';
import { useFilterData } from './use-filter-data';
import { useFilterHandlers } from './use-filter-handlers';
import { useActiveSelections } from './use-active-selections';
import { useFilterPersistence } from './use-filter-persistence';
import { useDebouncedValue } from '@/lib/utils';

/**
 * Core hook for managing image filter state and handlers
 */
export const useImageFiltersCore = (): UseImageFiltersReturn => {
  // Type selection - initialize with null instead of "Photo" to require user selection
  const [selectedType, setSelectedType] = useState<ImageType | null>(null);
  
  // Selected style - single selection state - initialize as null instead of empty string
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);
  
  // Selected influence - single selection state - initialize as null instead of empty string
  const [selectedInfluence, setSelectedInfluence] = useState<string | null>(null);
  
  // Background options - with single selection - initialize as null instead of empty string
  const [selectedBackground, setSelectedBackground] = useState<string | null>(null);

  // Width and height state with defaults
  const [width, setWidth] = useState<string>("1024");
  const [height, setHeight] = useState<string>("1024");

  // Image description state
  const [imageDescription, setImageDescription] = useState<string>('');
  
  // Debounced image description for search operations (500ms delay)
  const debouncedImageDescription = useDebouncedValue(imageDescription, 500);

  // Simple/Advanced mode state - default to Simple (false)
  const [isAdvancedMode, setIsAdvancedMode] = useState<boolean>(false);

  // Get filter options and loading states
  const { 
    filterOptions, 
    setFilterOptions, 
    isLoading 
  } = useFilterData(
    selectedType, 
    selectedStyle, 
    selectedInfluence, 
    selectedBackground
  );

  // Extract filter options
  const { 
    styleOptions, 
    influenceOptions, 
    colorOptions,
    backgroundOptions,
    environmentOptions,
    moodOptions
  } = filterOptions;

  // Extract setter functions
  const { 
    setStyleOptions, 
    setInfluenceOptions, 
    setColorOptions,
    setBackgroundOptions,
    setEnvironmentOptions,
    setMoodOptions
  } = setFilterOptions;

  // Get filter handlers
  const { 
    handleTypeChange,
    handleStyleChange: originalHandleStyleChange,
    handleInfluenceChange: originalHandleInfluenceChange,
    handleColorChange,
    handleBackgroundChange: originalHandleBackgroundChange,
    handleEnvironmentChange,
    handleMoodChange,
    resetAllFilters
  } = useFilterHandlers({
    setSelectedType,
    setSelectedStyle,
    setSelectedInfluence,
    setSelectedBackground,
    styleOptions,
    influenceOptions,
    colorOptions,
    backgroundOptions,
    environmentOptions,
    moodOptions,
    setStyleOptions,
    setInfluenceOptions,
    setColorOptions,
    setBackgroundOptions,
    setEnvironmentOptions,
    setMoodOptions
  });

  // Wrapper functions to adapt the interface
  const handleStyleChange = (options: FilterOption[]) => {
    originalHandleStyleChange(options);
    // Update selectedStyle with the checked option - use null instead of empty string
    const checkedOption = options.find(o => o.checked);
    if (checkedOption) {
      setSelectedStyle(checkedOption.id);
    } else {
      setSelectedStyle(null);
    }
  };

  const handleInfluenceChange = (options: FilterOption[]) => {
    originalHandleInfluenceChange(options);
    // Update selectedInfluence with the checked option - use null instead of empty string
    const checkedOption = options.find(o => o.checked);
    if (checkedOption) {
      setSelectedInfluence(checkedOption.id);
    } else {
      setSelectedInfluence(null);
    }
  };

  const handleBackgroundChange = (options: FilterOption[]) => {
    originalHandleBackgroundChange(options);
    // Update selectedBackground with the checked option - use null instead of empty string
    const checkedOption = options.find(o => o.checked);
    if (checkedOption) {
      setSelectedBackground(checkedOption.id);
    } else {
      setSelectedBackground(null);
    }
  };

  // Get active selections helpers
  const {
    getActiveSelections,
    getActiveBackgroundOptions,
  } = useActiveSelections({
    selectedStyle,
    selectedInfluence,
    selectedBackground,
    colorOptions,
    backgroundOptions,
    environmentOptions
  });

  // Use the localStorage hook to save/restore filter settings
  useFilterPersistence({
    selectedType,
    selectedStyle,
    setSelectedStyle,
    styleOptions,
    setStyleOptions,
    selectedInfluence,
    setSelectedInfluence,
    influenceOptions,
    setInfluenceOptions,
    selectedBackground,
    setSelectedBackground,
    backgroundOptions,
    setBackgroundOptions,
    colorOptions,
    setColorOptions,
    environmentOptions,
    setEnvironmentOptions,
    moodOptions,
    setMoodOptions,
    imageDescription,
    setImageDescription,
    width,
    height,
    setWidth,
    setHeight,
    setSelectedType,
    isAdvancedMode,
    setIsAdvancedMode
  });

  return {
    // Basic filter options
    selectedType,
    styleOptions,
    selectedStyle,
    setSelectedStyle,
    influenceOptions,
    selectedInfluence,
    setSelectedInfluence,
    colorOptions,
    backgroundOptions,
    selectedBackground,
    environmentOptions,
    moodOptions,
    
    // Image dimension options
    width,
    height,
    setWidth,
    setHeight,
    
    // Image description (both immediate and debounced)
    imageDescription,
    setImageDescription,
    debouncedImageDescription,
    
    // Simple/Advanced mode
    isAdvancedMode,
    setIsAdvancedMode,
    
    // Handlers
    handleTypeChange,
    handleStyleChange,
    handleInfluenceChange,
    handleColorChange,
    handleBackgroundChange,
    handleEnvironmentChange,
    handleMoodChange,
    resetAllFilters,
    
    // Helpers
    getActiveSelections,
    getActiveBackgroundOptions,
    isLoading: isLoading.style || isLoading.influence || isLoading.color || isLoading.background || isLoading.environment || isLoading.mood
  };
};
